import { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { ErlangAInputs } from '../types/erlang';
import {
  serviceLevel,
  asa,
  erlangC,
  findMinAgentsC,
  findMinAgentsA,
  serviceLevelA,
  erlangACompute,
} from '../utils/erlangEngine';
import { InputField } from './ErlangCTab';
import MetricCard from './MetricCard';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface AgentImpactRow {
  delta: number;
  agents: number;
  sl: number;
  asaVal: number;
  pw: number;
  occ: number;
  abandonRate?: number;
}

export default function SensitivityTab() {
  const [inputs, setInputs] = useState<ErlangAInputs>({
    callsPerInterval: 350,
    ahtSeconds: 240,
    intervalMinutes: 30,
    targetSLPercent: 80,
    targetAnswerTime: 20,
    shrinkagePercent: 30,
    costPerAgentHour: 25,
    avgPatienceTime: 120,
  });

  const [useErlangA, setUseErlangA] = useState(false);
  const [spreadRange, setSpreadRange] = useState(8);

  const intervalSeconds = inputs.intervalMinutes * 60;
  const A = (inputs.callsPerInterval * inputs.ahtSeconds) / intervalSeconds;
  const targetSL = inputs.targetSLPercent / 100;

  // Compute baseline agents
  const baseAgents = useMemo(() => {
    if (useErlangA) {
      return findMinAgentsA(A, targetSL, inputs.targetAnswerTime, inputs.ahtSeconds, inputs.avgPatienceTime);
    }
    return findMinAgentsC(A, targetSL, inputs.targetAnswerTime, inputs.ahtSeconds);
  }, [A, targetSL, inputs.targetAnswerTime, inputs.ahtSeconds, inputs.avgPatienceTime, useErlangA]);

  // Build impact table: from -spreadRange to +spreadRange agents
  const impactRows = useMemo((): AgentImpactRow[] => {
    const rows: AgentImpactRow[] = [];
    for (let delta = -spreadRange; delta <= spreadRange; delta++) {
      const agents = baseAgents + delta;
      if (agents < 1 || (!useErlangA && agents < Math.ceil(A))) continue; // Erlang A is stable below the load

      let sl: number, asaVal: number, pw: number, abandonRate: number | undefined;

      if (useErlangA) {
        sl = serviceLevelA(agents, A, inputs.ahtSeconds, inputs.avgPatienceTime, inputs.targetAnswerTime);
        const aRes = erlangACompute(agents, A, inputs.ahtSeconds, inputs.avgPatienceTime);
        asaVal = aRes.avgWait;
        pw = aRes.pw;
        abandonRate = aRes.abandonRate;
      } else {
        sl = serviceLevel(agents, A, inputs.targetAnswerTime, inputs.ahtSeconds);
        asaVal = agents > A ? (erlangC(agents, A) * inputs.ahtSeconds) / (agents - A) : 999;
        pw = agents > A ? erlangC(agents, A) : 1;
        abandonRate = undefined;
      }

      const occ = (A * (1 - (abandonRate ?? 0))) / agents; // abandoners are never handled
      rows.push({ delta, agents, sl, asaVal, pw, occ, abandonRate });
    }
    return rows;
  }, [baseAgents, A, inputs, useErlangA, spreadRange]);

  const update = (field: keyof ErlangAInputs, value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      setInputs((prev) => ({ ...prev, [field]: num }));
    }
  };

  // Chart data
  const chartLabels = impactRows.map((r) => (r.delta >= 0 ? `+${r.delta}` : `${r.delta}`));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950/40 to-purple-950/40 border border-blue-700/20 rounded-lg p-4">
        <h2 className="text-lg font-bold text-white mb-1">The Power of One</h2>
        <p className="text-sm text-gray-400">
          What happens when you lose 1, 2, 3 agents? The Erlang curve is non-linear — small changes in staffing
          produce dramatic swings in service level and wait times. This is why WFM matters.
        </p>
        <div className="mt-3 grid grid-cols-3 gap-4 text-center">
          <MetricCard label="Traffic Intensity" value={A.toFixed(2)} sublabel="Erlangs" />
          <MetricCard label="Baseline Agents" value={baseAgents} highlight sublabel={`for ${inputs.targetSLPercent}/${inputs.targetAnswerTime} SL`} />
          <MetricCard label="Occupancy at Baseline" value={(A / baseAgents * 100).toFixed(1) + '%'} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Inputs */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Inputs</h3>
          <InputField label="Calls per Interval" value={inputs.callsPerInterval} onChange={(v) => update('callsPerInterval', v)} />
          <InputField label="AHT (seconds)" value={inputs.ahtSeconds} onChange={(v) => update('ahtSeconds', v)} />
          <InputField label="Interval Length (min)" value={inputs.intervalMinutes} onChange={(v) => update('intervalMinutes', v)} />
          <InputField label="Target SL %" value={inputs.targetSLPercent} onChange={(v) => update('targetSLPercent', v)} />
          <InputField label="Target Answer Time (s)" value={inputs.targetAnswerTime} onChange={(v) => update('targetAnswerTime', v)} />
          <InputField label="Shrinkage %" value={inputs.shrinkagePercent} onChange={(v) => update('shrinkagePercent', v)} />

          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={useErlangA}
              onChange={(e) => setUseErlangA(e.target.checked)}
              className="rounded border-gray-600 accent-cyan-500"
            />
            Use Erlang A model
          </label>

          {useErlangA && (
            <InputField label="Avg Patience Time (s)" value={inputs.avgPatienceTime} onChange={(v) => update('avgPatienceTime', v)} />
          )}

          <hr className="border-gray-700/50" />
          <div>
            <label className="text-xs text-gray-400 block mb-1">Agent Spread: +/-{spreadRange}</label>
            <input
              type="range"
              min={3}
              max={15}
              value={spreadRange}
              onChange={(e) => setSpreadRange(parseInt(e.target.value))}
              className="w-full accent-blue-500"
            />
          </div>
        </div>

        {/* Charts + Table */}
        <div className="lg:col-span-3 space-y-6">
          {/* SL + ASA dual chart — THE headline */}
          <div className="bg-navy-950/50 border border-gray-700/50 rounded-lg p-4">
            <Line
              data={{
                labels: chartLabels,
                datasets: [
                  {
                    label: 'Service Level %',
                    data: impactRows.map((r) => r.sl * 100),
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    fill: true,
                    tension: 0.3,
                    pointRadius: 4,
                    pointBackgroundColor: impactRows.map((r) =>
                      r.delta === 0 ? '#22d3ee' : r.sl < targetSL ? '#ef4444' : '#3b82f6'
                    ),
                    borderWidth: 2,
                    yAxisID: 'y',
                  },
                  {
                    label: 'ASA (seconds)',
                    data: impactRows.map((r) => Math.min(r.asaVal, 300)),
                    borderColor: '#22d3ee',
                    borderDash: [5, 3],
                    tension: 0.3,
                    pointRadius: 3,
                    pointBackgroundColor: impactRows.map((r) =>
                      r.delta === 0 ? '#22d3ee' : '#22d3ee'
                    ),
                    borderWidth: 2,
                    yAxisID: 'y1',
                  },
                ],
              }}
              options={{
                responsive: true,
                interaction: { intersect: false, mode: 'index' },
                plugins: {
                  title: {
                    display: true,
                    text: 'Impact of Gaining/Losing Agents on Service Level & Wait Time',
                    color: '#e5e5e5',
                    font: { size: 14, weight: 'bold' },
                  },
                  legend: { labels: { color: '#9ca3af' } },
                  tooltip: {
                    callbacks: {
                      title: (ctx) => {
                        const idx = ctx[0].dataIndex;
                        const row = impactRows[idx];
                        return `${row.agents} agents (${row.delta >= 0 ? '+' : ''}${row.delta} from baseline)`;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    title: { display: true, text: 'Agents vs Baseline', color: '#9ca3af' },
                    ticks: { color: '#6b7280' },
                    grid: { color: 'rgba(75, 85, 99, 0.2)' },
                  },
                  y: {
                    type: 'linear',
                    position: 'left',
                    title: { display: true, text: 'Service Level %', color: '#3b82f6' },
                    ticks: { color: '#3b82f6', callback: (v) => v + '%' },
                    grid: { color: 'rgba(75, 85, 99, 0.2)' },
                    min: 0,
                    max: 100,
                  },
                  y1: {
                    type: 'linear',
                    position: 'right',
                    title: { display: true, text: 'ASA (seconds)', color: '#22d3ee' },
                    ticks: { color: '#22d3ee' },
                    grid: { drawOnChartArea: false },
                    min: 0,
                  },
                },
              }}
              plugins={[
                {
                  id: 'targetLine',
                  afterDraw(chart) {
                    const yScale = chart.scales.y;
                    const yPos = yScale.getPixelForValue(targetSL * 100);
                    const ctx = chart.ctx;
                    ctx.save();
                    ctx.strokeStyle = '#ef4444';
                    ctx.lineWidth = 1.5;
                    ctx.setLineDash([6, 4]);
                    ctx.beginPath();
                    ctx.moveTo(chart.chartArea.left, yPos);
                    ctx.lineTo(chart.chartArea.right, yPos);
                    ctx.stroke();
                    ctx.fillStyle = '#ef4444';
                    ctx.font = '11px Inter';
                    ctx.fillText(`Target: ${(targetSL * 100).toFixed(0)}%`, chart.chartArea.right - 80, yPos - 6);
                    ctx.restore();
                  },
                },
                {
                  id: 'baselineLine',
                  afterDraw(chart) {
                    const xScale = chart.scales.x;
                    const baseIdx = impactRows.findIndex((r) => r.delta === 0);
                    if (baseIdx < 0) return;
                    const xPos = xScale.getPixelForValue(baseIdx);
                    const ctx = chart.ctx;
                    ctx.save();
                    ctx.strokeStyle = '#22d3ee';
                    ctx.lineWidth = 1.5;
                    ctx.setLineDash([4, 3]);
                    ctx.beginPath();
                    ctx.moveTo(xPos, chart.chartArea.top);
                    ctx.lineTo(xPos, chart.chartArea.bottom);
                    ctx.stroke();
                    ctx.fillStyle = '#22d3ee';
                    ctx.font = '11px Inter';
                    ctx.fillText('Baseline', xPos + 4, chart.chartArea.top + 14);
                    ctx.restore();
                  },
                },
              ]}
            />
          </div>

          {/* Occupancy chart */}
          <div className="bg-navy-950/50 border border-gray-700/50 rounded-lg p-4">
            <Line
              data={{
                labels: chartLabels,
                datasets: [
                  {
                    label: 'Occupancy %',
                    data: impactRows.map((r) => r.occ * 100),
                    borderColor: '#8b5cf6',
                    backgroundColor: impactRows.map((r) =>
                      r.occ > 0.88 ? 'rgba(245, 158, 11, 0.2)' : 'rgba(139, 92, 246, 0.1)'
                    ),
                    fill: true,
                    tension: 0.3,
                    pointRadius: 4,
                    pointBackgroundColor: impactRows.map((r) =>
                      r.occ > 0.88 ? '#22d3ee' : '#8b5cf6'
                    ),
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: {
                  title: { display: true, text: 'Occupancy by Agent Count', color: '#e5e5e5', font: { size: 14, weight: 'bold' } },
                  legend: { display: false },
                },
                scales: {
                  x: { title: { display: true, text: 'Agents vs Baseline', color: '#9ca3af' }, ticks: { color: '#6b7280' }, grid: { color: 'rgba(75, 85, 99, 0.2)' } },
                  y: { title: { display: true, text: 'Occupancy %', color: '#9ca3af' }, ticks: { color: '#6b7280', callback: (v) => v + '%' }, grid: { color: 'rgba(75, 85, 99, 0.2)' }, min: 50, max: 100 },
                },
              }}
            />
          </div>

          {/* Impact Table */}
          <div>
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Agent Impact Detail</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-400 uppercase tracking-wider border-b border-gray-700/50">
                    <th className="text-center py-2 px-2">Delta</th>
                    <th className="text-right py-2 px-2">Agents</th>
                    <th className="text-right py-2 px-2">SL%</th>
                    <th className="text-right py-2 px-2">ASA</th>
                    <th className="text-right py-2 px-2">P(Wait)</th>
                    <th className="text-right py-2 px-2">Occ%</th>
                    {useErlangA && <th className="text-right py-2 px-2">Abandon%</th>}
                  </tr>
                </thead>
                <tbody>
                  {impactRows.map((r) => {
                    const isBaseline = r.delta === 0;
                    const belowTarget = r.sl < targetSL;
                    return (
                      <tr
                        key={r.delta}
                        className={`border-b border-gray-800/30 ${
                          isBaseline
                            ? 'bg-cyan-500/10 font-semibold'
                            : belowTarget
                            ? 'bg-red-950/20'
                            : 'hover:bg-navy-900/30'
                        }`}
                      >
                        <td className={`py-1.5 px-2 text-center font-mono ${
                          r.delta < 0 ? 'text-red-400' : r.delta > 0 ? 'text-emerald-400' : 'text-cyan-400'
                        }`}>
                          {r.delta === 0 ? 'BASE' : r.delta > 0 ? `+${r.delta}` : r.delta}
                        </td>
                        <td className="py-1.5 px-2 text-right text-white">{r.agents}</td>
                        <td className={`py-1.5 px-2 text-right font-semibold ${belowTarget ? 'text-red-400' : 'text-emerald-400'}`}>
                          {(r.sl * 100).toFixed(1)}%
                        </td>
                        <td className={`py-1.5 px-2 text-right ${r.asaVal > 30 ? 'text-red-400' : 'text-gray-300'}`}>
                          {r.asaVal > 300 ? '>5min' : r.asaVal.toFixed(1) + 's'}
                        </td>
                        <td className="py-1.5 px-2 text-right text-gray-400">{(r.pw * 100).toFixed(1)}%</td>
                        <td className={`py-1.5 px-2 text-right ${r.occ > 0.88 ? 'text-cyan-400' : 'text-gray-300'}`}>
                          {(r.occ * 100).toFixed(1)}%
                        </td>
                        {useErlangA && (
                          <td className={`py-1.5 px-2 text-right ${(r.abandonRate ?? 0) > 0.05 ? 'text-red-400' : 'text-gray-400'}`}>
                            {r.abandonRate !== undefined ? (r.abandonRate * 100).toFixed(2) + '%' : '—'}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
